import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { SectionTourTrigger } from '../tour/SectionTourTrigger'
import { CircularProgress, parseMonthToDateRange, OTIFComparisonCard, SpendComparisonCard, QualityMetricComparisonCard, DeliveryThresholds, QualityThresholds, QualityGradeBands } from './SiteKPIComponents'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'
import { calculateTrendLine, getPercentageDomain } from './dashboardUtils'
import { drilldownModeParams } from './dashboardFilterUtils'

/**
 * Custom tooltip for the OTIF chart - matches DefaultTab implementation.
 * Shows count breakdown (Passed, Failed, Arrears, Total) then percentages.
 */
type ChartTooltipItem = {
  dataKey?: string
  name?: string
  value?: number | string | null
  color?: string
  payload?: Record<string, number | string | null | undefined>
}

function OTIFChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const toNum = (val: number | string | null | undefined): number => {
    if (val == null || val === '') return 0
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isFinite(n) ? n : 0
  }

  const row = payload[0]?.payload
  const otifCount = toNum(byKey['otifCount']?.value)
  const failed = toNum(byKey['failed']?.value)
  const arrears = toNum(byKey['arrears']?.value)
  const total = toNum(row?.['totalLines']) || (otifCount + failed + arrears)
  const otifFromBackend = row?.['otif']
  const otif = otifFromBackend != null && otifFromBackend !== '' ? toNum(otifFromBackend) : null
  const target = toNum(byKey['target']?.value)
  const trend = byKey['otifTrend']?.value

  type Row = { label: string; value: string; color?: string }
  const rows: Row[] = [
    { label: 'Passed OTIF PO Lines', value: String(Math.round(otifCount)), color: byKey['otifCount']?.color },
    { label: 'Failed OTIF PO Lines', value: String(Math.round(failed)), color: byKey['failed']?.color },
    { label: 'Arrears PO Lines', value: String(Math.round(arrears)), color: byKey['arrears']?.color },
    { label: 'Total PO Lines', value: String(Math.round(total)), color: '#6b7280' },
    { label: 'OTIF %', value: otif !== null ? `${otif.toFixed(2)}%` : '-', color: byKey['otif']?.color },
    { label: 'Target %', value: `${target.toFixed(2)}%`, color: byKey['target']?.color },
  ]
  if (trend != null && trend !== '') {
    rows.push({ label: 'Trend %', value: `${toNum(trend).toFixed(2)}%`, color: byKey['otifTrend']?.color })
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{ margin: 0, padding: 10, backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
    >
      {label && <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>}
      <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {rows.map((row) => (
          <li key={row.label} className="recharts-tooltip-item" style={{ display: 'block', paddingTop: 4, paddingBottom: 4, color: row.color }}>
            <span className="recharts-tooltip-item-name">{row.label}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// CR-3837: Extended props to support supplier type filter callback
// CR-5912: the previously-embedded supplier-type filter is removed in favour
// of the page-level DashboardFilters, which carries the same 20-filter set
// the main dashboard uses. Dashboard.tsx force-opens that panel when this
// tab is active so users see the full filter row without clicking Show Filters.
type SiteKPITabProps = TabProps;

// CR-6287: `currency` prop dropped from the destructure — the Arrears-by-value
// title no longer interpolates it (see below). It stays on TabProps for the
// other tabs that still consume it.
export function SiteKPITab({ data, filters }: SiteKPITabProps) {
  const { formatCurrency, formatCompactCurrency } = useFormatters()
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. 0 keeps whole-number ring labels; 1-2 shows e.g. 8.6%.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const qualityLabel = useQualityMeasureLabel()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const deliveryMode = data.delivery_metric_mode ?? 'both'
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  const [activeSiteKPITab, setActiveSiteKPITab] = useState('delivery')
  // CR-3837: Use filter from props if available, with local state for UI
  // CR-5912: supplierFilter local was used by the removed in-tab dropdown.
  // The page-level DashboardFilters owns supplierType now; this tab reads
  // filtered data through `data` and doesn't need to mirror the value.

  // State for hover-to-highlight on OTIF chart legend (matches DefaultTab)
  const [hoveredOtifSeries, setHoveredOtifSeries] = useState<string | null>(null)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // OTIF specialized drill-down modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [otifModalInitialTab, setOtifModalInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total' | undefined>(undefined)
  const [otifModalFilters, setOtifModalFilters] = useState<Record<string, string>>({})

  // OTD specialized drill-down modal state
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)
  const [otdModalInitialTab, setOtdModalInitialTab] = useState<'on_time' | 'early' | 'late' | 'total' | undefined>(undefined)
  const [otdModalFilters, setOtdModalFilters] = useState<Record<string, string>>({})

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    // W3-T5: same capped window and arrears term as the charts above.
    ...drilldownModeParams(filters),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    // CR-7229: carry the warehouse scope into the drill-downs so the detail
    // list matches the tile it was opened from.
    ...(filters?.warehouse && { warehouse: filters.warehouse }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
    // Customer review 11 Sep (item 34): Internal / External reaches the drills.
    ...(filters?.supplierType && { supplier_type: filters.supplierType }),
  }

  // Handle chart click - open drill-down modal with month-specific data
  const handleChartClick = (chartData: RechartsClickData | null, metricType: 'ppm' | 'otif' | 'otd' | 'spend' | 'arrears' | 'ncrs' | 'appeals') => {
    if (!chartData) return
    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || (chartData.activePayload[0].payload?.['site'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    // For OTIF, use the specialized OTIF drill-down modal with month-specific filters
    if (metricType === 'otif') {
      let initialTab: 'failed' | 'arrears' | 'positive' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'failed') { initialTab = 'failed'; break }
          else if (item.dataKey === 'arrears') { initialTab = 'arrears'; break }
          else if (item.dataKey === 'otifCount') { initialTab = 'positive'; break }
        }
      }
      setOtifModalFilters(chartFilters)
      setOtifModalContext(month)
      setOtifModalInitialTab(initialTab)
      setIsOTIFModalOpen(true)
      return
    }

    // For OTD (depth of delivery), use the specialized OTD drill-down modal with month-specific filters
    if (metricType === 'otd') {
      let initialTab: 'on_time' | 'early' | 'late' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'onTime') { initialTab = 'on_time'; break }
          else if (item.dataKey === 'early') { initialTab = 'early'; break }
          else if (item.dataKey === 'late') { initialTab = 'late'; break }
        }
      }
      setOtdModalFilters(chartFilters)
      setOtdModalContext(month)
      setOtdModalInitialTab(initialTab)
      setIsOTDModalOpen(true)
      return
    }

    // Map metric types to drilldown keys
    const metricKeyMap: Record<string, string> = {
      'ppm': 'ppm',
      'spend': 'spend',
      'arrears': 'arrears',
      'ncrs': 'ncr',
      'appeals': 'appeals',
    }

    const metricKey = metricKeyMap[metricType]
    if (metricKey) {
      // CR-6676: scope the NCR drill-down to the clicked "Live NCRs Overdue"
      // segment so its record count matches the bar (overdue by default; fall
      // back to pending for a pending-only month so it isn't empty).
      if (metricType === 'ncrs') {
        const point = data.charts?.liveNCRsOverdue?.find(d => d.month === month)
        const ncr_lifecycle = (point?.overdue ?? 0) > 0 ? 'overdue' : 'pending'
        openDrilldown(metricKey, { ...chartFilters, ncr_lifecycle }, month)
        return
      }
      openDrilldown(metricKey, chartFilters, month)
    }
  }

  // Get site KPI data with defaults
  const siteKPIData = data.siteKPI || {
    otifBySite: [],
    ppmBySite: [],
    ncrBreakdown: [],
    deliveryBySite: [],
  }

  // CR-4211 / CR-5873: configurable targets from SupplierPerformanceSettings
  const ppmTarget = data.targets?.ppm ?? data.quality?.ppmTarget ?? 2000

  // Three-tier color thresholds for KPI cards (green/yellow/red)
  const otifThresholds: DeliveryThresholds = data.targets?.otif_grade_bands ?? []
  // CR-6652: configurable PPM colour bands (replaces three-tier thresholds)
  const ppmGradeBands: QualityGradeBands = data.targets?.ppm_grade_bands ?? []
  // Legacy three-tier thresholds (deprecated, kept for backward compatibility)
  const ppmThresholds: QualityThresholds = {
    excellent: data.targets?.ppm_excellent ?? 500,
    good: data.targets?.ppm_good ?? 2000,
    poor: data.targets?.ppm_poor ?? 5000,
  }


  // Spend values: sum from spend-by-month chart data for period accuracy
  const spendByMonthData: Array<{ month: string; spend: number }> = data.charts?.spendByMonth || []
  const spendFromChart = (months: number) => {
    const recent = spendByMonthData.slice(-months)
    return recent.reduce((sum, m) => sum + (m.spend || 0), 0)
  }
  const spendValues = {
    MONTH: spendByMonthData.length > 0 ? spendFromChart(1) : (data.cost?.totalSpend || 0),
    '3 MONTH': spendByMonthData.length > 0 ? spendFromChart(3) : (data.cost?.totalSpend || 0),
    '12 MONTH': spendByMonthData.length > 0 ? spendFromChart(12) : (data.cost?.totalSpend || 0),
  }

  // OTIF comparison values for side-by-side display.
  // CR-6543: pool the backend counts (Σpassed / Σdenominator) across the
  // window instead of an unweighted mean of monthly percentages — a month
  // with 3 lines must not weigh the same as a month with 3,000, and the
  // 12-month figure must agree with the Default tab's pooled ring.
  const otifTrendDataForCalc: Array<{ month: string; otif: number | null; otifCount?: number | null; denominator?: number | null }> = data.charts?.otifData || []
  // METRIC-DEFINITIONS Q13 (wave 3a): an empty denominator is N/A (null),
  // never 0; the pooled figure is Σpassed / Σdenominator over the months.
  const otifFromChart = (months: number): number | null => {
    const recent = otifTrendDataForCalc.slice(-months)
    if (recent.length === 0) return data.quality?.otif ?? null
    const passed = recent.reduce((acc, m) => acc + (m.otifCount || 0), 0)
    const denominator = recent.reduce((acc, m) => acc + (m.denominator || 0), 0)
    return denominator > 0 ? (passed / denominator) * 100 : null
  }
  const otifComparisonValues = {
    month: otifTrendDataForCalc.length > 0 ? otifFromChart(1) : (data.quality?.otif ?? null),
    threeMonth: otifTrendDataForCalc.length > 0 ? otifFromChart(3) : (data.quality?.otif ?? null),
    twelveMonth: otifTrendDataForCalc.length > 0 ? otifFromChart(12) : (data.quality?.otif ?? null),
  }

  // Spend comparison values for side-by-side display
  const spendComparisonValues = {
    month: spendValues.MONTH,
    threeMonth: spendValues['3 MONTH'],
    twelveMonth: spendValues['12 MONTH'],
  }

  // PPM comparison values for side-by-side display
  const ppmTrendDataForCalc: Array<{ month: string; ppm: number }> = data.charts?.ppmTrend || []
  const ppmFromChart = (months: number) => {
    const recent = ppmTrendDataForCalc.slice(-months)
    if (recent.length === 0) return data.quality?.ppm || 0
    const sum = recent.reduce((acc, m) => acc + (m.ppm || 0), 0)
    return Math.round(sum / recent.length)
  }
  const ppmComparisonValues = {
    month: ppmTrendDataForCalc.length > 0 ? ppmFromChart(1) : (data.quality?.ppm || 0),
    threeMonth: ppmTrendDataForCalc.length > 0 ? ppmFromChart(3) : (data.quality?.ppm || 0),
    twelveMonth: ppmTrendDataForCalc.length > 0 ? ppmFromChart(12) : (data.quality?.ppm || 0),
  }

  // CR-3837: Use API data for appeals breakdown instead of hardcoded values
  const appealsData = data.delivery?.appealsBreakdown || {
    raised_by_customer: 0,
    raised_by_supplier: 0,
    accepted: 0,
    rejected: 0,
    auto_approved: 0,
  }

  // OTIF trend data - targets now come from API via SupplierPerformanceSettings
  // Calculate trend line to match DefaultTab visualization
  const otifTrendData = data.charts?.otifData || []
  const otifDataWithTrend = calculateTrendLine(otifTrendData, 'otif', 'otifTrend')
  const otifDomain = getPercentageDomain(otifDataWithTrend, 'otif')

  // CR-3837: Use API data for appeals trend instead of hardcoded values
  const appealsTrendData = data.charts?.appealsTrend || []

  // Spend trend data
  const spendTrendData = data.charts?.spendByMonth || []

  // Depth of delivery data
  const depthOfDeliveryData = siteKPIData.deliveryBySite

  // Arrears by lines data — CR-6286: the Site KPI fetch (dashboard_stats) does
  // not return charts.arrearsDetails (that dataset only exists on the lazy modal
  // endpoint), so the graph was always blank. Reuse the per-month arrears
  // PO-line count already carried in otifData — the same 'arrears' series the
  // OTIF graph plots — shaped to { month, lines } for this chart.
  const arrearsByLinesData = otifTrendData.map((m) => ({ month: m.month, lines: m.arrears }))

  // Arrears by value data
  const arrearsByValueData = data.charts?.historicalArrears || []

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="dashboard"
          sectionKey="site-kpi"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* CR-5912: embedded Supplier-Type-only filter removed. The page-level
          DashboardFilters (above this tab) is auto-opened on Site KPI and
          carries the same 20-filter set as the main dashboard, including
          Supplier Type. */}

      {/* Sub-tabs for Delivery and Quality */}
      <div className="bg-card rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveSiteKPITab('delivery')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeSiteKPITab === 'delivery'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Delivery
            </button>
            <button
              onClick={() => setActiveSiteKPITab('quality')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeSiteKPITab === 'quality'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Quality
            </button>
          </nav>
        </div>
        <div className="p-6">
          {activeSiteKPITab === 'delivery' && (
            <div className="space-y-6">
              {/* KPI Metrics Row - OTIF, APPEALS, SPEND */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {/* OTIF Card - Side-by-side comparison of all periods */}
                {showOTIF && (
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">OTIF</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <OTIFComparisonCard values={otifComparisonValues} thresholds={otifThresholds} />
                  </CardContent>
                </Card>
                )}

                {/* APPEALS Card - CR-3836: Using CSS Grid for equal column distribution */}
                <Card className="shadow-sm overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">APPEALS</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2 px-4">
                    <div className="grid grid-cols-5 gap-2">
                      <div
                        className="flex flex-col items-center justify-center py-2 px-1 bg-blue-50 rounded-md cursor-pointer hover:bg-blue-100 transition-all"
                        onClick={() => openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'true' }, 'Raised by Customer')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'true' }, 'Raised by Customer'); } }}
                      >
                        <div className="text-[9px] text-muted-foreground text-center leading-tight mb-1">
                          Raised by<br/>Customer
                        </div>
                        <div className="text-lg font-bold text-blue-600 tabular-nums">{appealsData.raised_by_customer}</div>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center py-2 px-1 bg-purple-50 rounded-md cursor-pointer hover:bg-purple-100 transition-all"
                        onClick={() => openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'false' }, 'Raised by Supplier')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'false' }, 'Raised by Supplier'); } }}
                      >
                        <div className="text-[9px] text-muted-foreground text-center leading-tight mb-1">
                          Raised by<br/>Supplier
                        </div>
                        <div className="text-lg font-bold text-purple-600 tabular-nums">{appealsData.raised_by_supplier}</div>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center py-2 px-1 bg-green-50 rounded-md cursor-pointer hover:bg-green-100 transition-all"
                        onClick={() => openDrilldown('appeals', { ...drilldownFilters, status: 'accepted' }, 'Accepted Appeals')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, status: 'accepted' }, 'Accepted Appeals'); } }}
                      >
                        <div className="text-[9px] text-muted-foreground text-center leading-tight mb-1">
                          Accepted
                        </div>
                        <div className="text-lg font-bold text-green-600 tabular-nums">{appealsData.accepted}</div>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center py-2 px-1 bg-red-50 rounded-md cursor-pointer hover:bg-red-100 transition-all"
                        onClick={() => openDrilldown('appeals', { ...drilldownFilters, status: 'rejected' }, 'Rejected Appeals')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, status: 'rejected' }, 'Rejected Appeals'); } }}
                      >
                        <div className="text-[9px] text-muted-foreground text-center leading-tight mb-1">
                          Rejected
                        </div>
                        <div className="text-lg font-bold text-red-600 tabular-nums">{appealsData.rejected}</div>
                      </div>
                      <div
                        className="flex flex-col items-center justify-center py-2 px-1 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100 transition-all"
                        onClick={() => openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'true', status: 'accepted' }, 'Auto Approved Appeals')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown('appeals', { ...drilldownFilters, is_customer_appeal: 'true', status: 'accepted' }, 'Auto Approved Appeals'); } }}
                      >
                        <div className="text-[9px] text-muted-foreground text-center leading-tight mb-1">
                          Auto<br/>Approved
                        </div>
                        <div className="text-lg font-bold text-gray-600 tabular-nums">{appealsData.auto_approved}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* SPEND Card - Side-by-side comparison of all periods */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">SPEND</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SpendComparisonCard
                      values={spendComparisonValues}
                      formatCurrency={formatCurrency}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 1 - OTIF and Appeals */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {showOTIF && (
                <ChartWrapper
                  title="OTIF GRAPH"
                  data={otifDataWithTrend}
                  exportFilename="otif-graph"
                  legend={[
                    { color: '#1d4ed8', label: 'OTIF %', dataKey: 'otif' },
                    { color: '#dc2626', label: 'Target', dataKey: 'target' },
                    { color: '#f97316', label: 'Trend', dataKey: 'otifTrend' },
                    { color: '#10b981', label: 'OTIF PO Lines', dataKey: 'otifCount' },
                    { color: '#fbbf24', label: 'Arrears PO Lines', dataKey: 'arrears' },
                    { color: '#ef4444', label: 'Failed OTIF PO Lines', dataKey: 'failed' },
                  ]}
                  hoveredSeries={hoveredOtifSeries}
                  onSeriesHover={setHoveredOtifSeries}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart
                      data={otifDataWithTrend}
                      onClick={(chartData) => handleChartClick(chartData, 'otif')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otifDomain} allowDataOverflow={true} padding={{ top: 8 }} tickFormatter={(v) => `${v}%`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTIFChartTooltip />} />
                      <Bar yAxisId="right" dataKey="otifCount" stackId="a" fill="#10b981" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifCount' ? 0.2 : 1} name="OTIF PO Lines" />
                      <Bar yAxisId="right" dataKey="arrears" stackId="a" fill="#fbbf24" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'arrears' ? 0.2 : 1} name="Arrears PO Lines" />
                      <Bar yAxisId="right" dataKey="failed" stackId="a" fill="#ef4444" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'failed' ? 0.2 : 1} name="Failed OTIF PO Lines" />
                      <Line yAxisId="left" type="monotone" dataKey="otif" stroke="#1d4ed8" strokeWidth={3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otif' ? 0.2 : 1} dot={{ r: 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
                      <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={3} strokeDasharray="8 4" strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'target' ? 0.2 : 1} dot={false} name="Target" />
                      <Line yAxisId="left" type="monotone" dataKey="otifTrend" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifTrend' ? 0.2 : 1} dot={false} name="Trend" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartWrapper>
                )}

                <ChartWrapper
                  title="APPEALS GRAPH"
                  data={appealsTrendData}
                  exportFilename="appeals-graph"
                  legend={[
                    { color: '#3b82f6', label: 'Awaiting Customer' },
                    { color: '#10b981', label: 'Accepted' },
                    { color: '#f59e0b', label: 'Awaiting Supplier' },
                    { color: '#ef4444', label: 'Rejected' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={appealsTrendData}
                      onClick={(chartData) => handleChartClick(chartData, 'appeals')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="awaitingCustomer" stackId="a" fill="#3b82f6" name="Awaiting Customer" />
                      <Bar dataKey="accepted" stackId="a" fill="#10b981" name="Accepted" />
                      <Bar dataKey="awaitingSupplier" stackId="a" fill="#f59e0b" name="Awaiting Supplier" />
                      <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 2 - Spend and Depth of Delivery */}
              <div className={`grid gap-4 ${showOTD ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <ChartWrapper
                  title="SPEND BY MONTH GRAPH"
                  data={spendTrendData}
                  exportFilename="spend-by-month"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={spendTrendData}
                      onClick={(chartData) => handleChartClick(chartData, 'spend')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value: number) => formatCompactCurrency(value)}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        stroke="#3b82f6"
                        fill="#93c5fd"
                        name="Spend"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                {showOTD && (
                <ChartWrapper
                  title="DEPTH OF DELIVERY GRAPH"
                  data={depthOfDeliveryData}
                  exportFilename="depth-of-delivery"
                  legend={[
                    { color: '#10b981', label: 'On Time' },
                    { color: '#fbbf24', label: 'Early' },
                    { color: '#ef4444', label: 'Late' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={depthOfDeliveryData}
                      onClick={(chartData) => handleChartClick(chartData, 'otd')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      {/* CR-6286: data rows are keyed by `company` (from
                          company_kpi.delivery_by_company), not `site` — the old
                          dataKey never matched so the x-axis/bars were empty. */}
                      <XAxis dataKey="company" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="onTime" stackId="a" fill="#10b981" name="On Time" />
                      <Bar dataKey="early" stackId="a" fill="#fbbf24" name="Early" />
                      <Bar dataKey="late" stackId="a" fill="#ef4444" name="Late" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
                )}
              </div>

              {/* Charts Row 3 - Arrears by Lines and Arrears by Value */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title="ARREARS by LINES GRAPH"
                  data={arrearsByLinesData}
                  exportFilename="arrears-by-lines"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={arrearsByLinesData}
                      onClick={(chartData) => handleChartClick(chartData, 'arrears')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Line
                        type="monotone"
                        dataKey="lines"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ fill: '#ef4444', r: 4 }}
                        name="PO Lines"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  // CR-6287: static label mirroring the sibling "ARREARS by LINES
                  // GRAPH". The old title interpolated currency.currentCurrency,
                  // which renders "[object Object]" whenever the resolved currency
                  // is object-shaped rather than a plain code string.
                  title="ARREARS by VALUES GRAPH"
                  data={arrearsByValueData}
                  exportFilename="arrears-by-value"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={arrearsByValueData}
                      onClick={(chartData) => handleChartClick(chartData, 'arrears')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value: number) => formatCompactCurrency(value)}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6', r: 4 }}
                        name="Value"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>
            </div>
          )}

          {activeSiteKPITab === 'quality' && (
            <div className="space-y-6">
              {/* KPI Metrics Row - PPM, NCRs, Defect Rate */}
              <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {/* PPM Card - Side-by-side comparison of all periods */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{qualityLabel}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <QualityMetricComparisonCard
                      values={ppmComparisonValues}
                      target={ppmTarget}
                      bands={ppmGradeBands}
                      thresholds={ppmThresholds}
                    />
                  </CardContent>
                </Card>

                {/* NCRs Card */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">NCRs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-xs text-muted-foreground">Open</div>
                        <div className="text-lg font-bold text-amber-600">
                          {data.quality?.ncrsByStatus?.['open'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Closed</div>
                        <div className="text-lg font-bold text-green-600">
                          {data.quality?.ncrsByStatus?.['closed'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Overdue</div>
                        <div className="text-lg font-bold text-red-600">
                          {data.quality?.ncrsByStatus?.['overdue'] || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Total</div>
                        <div className="text-lg font-bold text-blue-600">
                          {data.quality?.ncrs || 0}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Defect Rate Card - derived from PPM */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">DEFECT RATE</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-2">
                      <CircularProgress
                        value={Math.min((data.quality?.ppm || 0) / 10000, 100)}
                        size={100}
                        strokeWidth={10}
                        color={data.quality?.ppm && data.quality.ppm > ppmTarget ? '#ef4444' : '#10b981'}
                        decimals={kpiDecimals}
                      />
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-1">
                      {((data.quality?.ppm || 0) / 10000).toFixed(kpiDecimals)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 1 - PPM Trend and NCR Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title={`${qualityLabel} TREND`}
                  data={data.charts?.ppmTrend || []}
                  exportFilename="ppm-trend"
                  legend={[
                    { color: '#ef4444', label: qualityLabel },
                    { color: '#3b82f6', label: 'Target' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={data.charts?.ppmTrend || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ppm')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      {/* CR-4537: interval={0} forces every month tick to render on long ranges; angle + smaller font keep them readable */}
                      <XAxis dataKey="month" tick={{ fontSize: (data.charts?.ppmTrend || []).length > 24 ? 9 : 11 }} interval={0} angle={(data.charts?.ppmTrend || []).length > 12 ? -45 : 0} textAnchor={(data.charts?.ppmTrend || []).length > 12 ? 'end' : 'middle'} height={(data.charts?.ppmTrend || []).length > 12 ? 55 : 30} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                        {(data.charts?.ppmTrend || []).map((entry, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.ppm <= entry.target ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                      <Line type="monotone" dataKey="target" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="NCR BREAKDOWN < 12 MONTHS"
                  data={siteKPIData.ncrBreakdown}
                  exportFilename="ncr-breakdown"
                  legend={[
                    { color: '#fbbf24', label: 'Open' },
                    { color: '#10b981', label: 'Closed' },
                    { color: '#ef4444', label: 'Overdue' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={siteKPIData.ncrBreakdown}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="open" stackId="a" fill="#fbbf24" name="Open" />
                      <Bar dataKey="closed" stackId="a" fill="#10b981" name="Closed" />
                      <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 2 - PPM by Site and NCR by Severity */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title={`${qualityLabel} BY SITE`}
                  data={siteKPIData.ppmBySite}
                  exportFilename="ppm-by-site"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={siteKPIData.ppmBySite}
                      onClick={(chartData) => handleChartClick(chartData, 'ppm')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="site" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                        {(siteKPIData.ppmBySite || []).map((entry, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.ppm <= (entry.target || 500) ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="NCRs BY SEVERITY"
                  data={Object.entries(data.quality?.ncrsBySeverity || {}).map(([key, value]) => ({
                    severity: key.charAt(0).toUpperCase() + key.slice(1),
                    count: value,
                  }))}
                  exportFilename="ncrs-by-severity"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={Object.entries(data.quality?.ncrsBySeverity || {}).map(([key, value]) => ({
                        severity: key.charAt(0).toUpperCase() + key.slice(1),
                        count: value,
                      }))}
                      layout="vertical"
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="severity" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="count" name="NCRs" radius={[0, 4, 4, 0]}>
                        {Object.entries(data.quality?.ncrsBySeverity || {}).map(([key], index) => {
                          const colors: Record<string, string> = {
                            critical: '#ef4444',
                            major: '#f59e0b',
                            minor: '#3b82f6',
                            low: '#10b981',
                          }
                          return <Cell key={`cell-${index}`} fill={colors[key] || '#6b7280'} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 3 - Live NCRs and Reject Value */}
              <div className="grid grid-cols-2 gap-4">
                <ChartWrapper
                  title="LIVE NCRs OVERDUE"
                  data={data.charts?.liveNCRsOverdue || []}
                  exportFilename="live-ncrs-overdue"
                  legend={[
                    { color: '#ef4444', label: 'Overdue' },
                    { color: '#f59e0b', label: 'Pending' },
                  ]}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={data.charts?.liveNCRsOverdue || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" />
                      <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="REJECT VALUE BY MONTH"
                  data={data.charts?.ppmNCRs?.slice(0, 6).map((ncr) => ({
                    month: ncr.date_raised?.substring(0, 7) || 'Unknown',
                    value: ncr.reject_value || 0,
                  })) || []}
                  exportFilename="reject-value-by-month"
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={data.charts?.ppmNCRs?.slice(0, 6).map((ncr) => ({
                        month: ncr.date_raised?.substring(0, 7) || 'Unknown',
                        value: ncr.reject_value || 0,
                      })) || []}
                      onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value: number) => formatCompactCurrency(value)}
                      />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#ef4444"
                        fill="#fecaca"
                        name="Reject Value"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={isOTIFModalOpen}
        onClose={() => setIsOTIFModalOpen(false)}
        filters={otifModalFilters}
        contextLabel={otifModalContext}
        initialTab={otifModalInitialTab}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        filters={otdModalFilters}
        contextLabel={otdModalContext}
        initialTab={otdModalInitialTab}
      />
    </div>
  )
}
